import {
  addDisplayName,
  addHandlers,
  addProps,
  addRef,
  addState,
  addStateHandlers,
  flowMax,
} from 'ad-hok'
import {
  ColDef,
  FilterChangedEvent,
  ICellRendererParams,
  RowClickedEvent,
} from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import cx from 'classnames'
import React, { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { addApi } from 'shared/src/api'
import { CreatorEarningsOverview } from 'shared/src/api/types'
import { addNavigate } from 'shared/src/utils/addNavigate'
import { addDataLoadingFromPromise } from 'shared/src/utils/dataLoading'
import { typedAs } from 'shared/src/utils/typedAs'

import { Button } from 'components/Button'
import { Page } from 'components/Page'
import { NumberFieldNew } from 'components/TextField'
import { addCmsLoadingAndErrorIndicators } from 'utils/addCmsLoadingAndErrorIndicators'
import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { makeDayEarningsRoute } from 'utils/routes'
import {
  COLUMN_TYPES,
  DEFAULT_COL_DEF,
  DOLLAR_AMOUNT_COLUMN_WIDTH,
  dateComparator,
  formatNumber,
  formatPrice,
  getOverviewAggregateRow,
  overviewTotalEarnings,
} from './utils'

const useStyles = createUseStyles({
  formRow: {
    display: 'flex',
    gap: 50,
  },
  actionColumn: {
    width: '250px',
  },
  table: {
    marginTop: 20,
    width: '100%',
    height: '90dvh',
  },
  commitButton: {
    width: '100%',
    backgroundColor: '#fac79b',
  },
  commitButtonDisabled: {
    width: '100%',
    backgroundColor: '#d9d9d9',
  },
})

export const CommitButtonRenderer: FC<ICellRendererParams> = flowMax(
  addDisplayName('CommitButtonRenderer'),
  addApi,
  addTranslationHelpers,
  addHandlers({
    onClickCommit:
      ({ data, api, t }) =>
      () => {
        if (
          window.confirm(
            t('admin.earnings.commitConfirmation', { date: data.date })
          )
        ) {
          api.creatorEarnings
            .commitDate({ date: data.date })
            .then(() => window.location.reload())
        }
      },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ onClickCommit, node, data: { isCommitted }, classes, t }) =>
    node.isRowPinned() ? (
      <></>
    ) : isCommitted ? (
      <button
        onClick={onClickCommit}
        disabled
        className={classes.commitButtonDisabled}
      >
        {t('admin.earnings.committedButton')}
      </button>
    ) : (
      <button onClick={onClickCommit} className={classes.commitButton}>
        {t('admin.earnings.commitButton')}
      </button>
    )
)

export const EarningsOverview: FC = flowMax(
  addDisplayName('EarningsOverview'),
  addApi,
  addDataLoadingFromPromise(
    'data',
    ({ api }) =>
      Promise.all([
        api.creatorEarnings.overview(),
        api.creatorFundCosts.mostRecent(),
      ]),
    []
  ),
  addCmsLoadingAndErrorIndicators('data'),
  addProps(({ data: [creatorEarningsOverview, creatorFundCosts] }) => ({
    creatorEarningsOverview,
    creatorFundCosts,
  })),
  addTranslationHelpers,
  addProps(({ t }) => ({
    columns: typedAs<ColDef<CreatorEarningsOverview>[]>([
      {
        headerName: t('admin.earnings.columns.date'),
        field: 'date',
        filter: 'agDateColumnFilter',
        filterParams: {
          comparator: dateComparator,
        },
        width: 150,
      },
      {
        headerName: t('admin.earnings.columns.totalEarnings'),
        type: ['number'],
        valueGetter: ({ data }) => data && overviewTotalEarnings(data),
        valueFormatter: formatPrice,
      },
      {
        headerName: t('admin.earnings.columns.committed'),
        field: 'isCommitted',
        cellRenderer: CommitButtonRenderer,
      },
      {
        headerName: t('admin.earnings.columns.mediaViews'),
        field: 'mediaViews',
        type: ['number'],
        valueFormatter: formatNumber,
      },
      {
        headerName: t('admin.earnings.columns.mediaViewEarnings'),
        field: 'mediaViewEarnings',
        type: ['number'],
        valueFormatter: formatPrice,
        width: DOLLAR_AMOUNT_COLUMN_WIDTH,
      },
      {
        headerName: t('admin.earnings.columns.productViews'),
        field: 'productViews',
        type: ['number'],
        valueFormatter: formatNumber,
      },
      {
        headerName: t('admin.earnings.columns.productViewEarnings'),
        field: 'productViewEarnings',
        type: ['number'],
        valueFormatter: formatPrice,
        width: DOLLAR_AMOUNT_COLUMN_WIDTH,
      },
      {
        headerName: t('admin.earnings.columns.outgoingProductClicks'),
        field: 'outgoingProductClicks',
        type: ['number'],
        valueFormatter: formatNumber,
      },
      {
        headerName: t('admin.earnings.columns.outgoingProductClickEarnings'),
        field: 'outgoingProductClickEarnings',
        type: ['number'],
        valueFormatter: formatPrice,
        width: DOLLAR_AMOUNT_COLUMN_WIDTH,
      },
      {
        headerName: t('admin.earnings.columns.mediaPublished'),
        field: 'mediaPublished',
        type: ['number'],
        valueFormatter: formatNumber,
      },
      {
        headerName: t('admin.earnings.columns.mediaPublishedEarnings'),
        field: 'mediaPublishedEarnings',
        type: ['number'],
        valueFormatter: formatPrice,
        width: DOLLAR_AMOUNT_COLUMN_WIDTH,
      },
      {
        headerName: t('admin.earnings.columns.mediaTagged'),
        field: 'mediaTagged',
        type: ['number'],
        valueFormatter: formatNumber,
      },
      {
        headerName: t('admin.earnings.columns.mediaTaggedEarnings'),
        field: 'mediaTaggedEarnings',
        type: ['number'],
        valueFormatter: formatPrice,
        width: DOLLAR_AMOUNT_COLUMN_WIDTH,
      },
      {
        headerName: t('admin.earnings.columns.editsCreated'),
        field: 'editsCreated',
        type: ['number'],
        valueFormatter: formatNumber,
      },
      {
        headerName: t('admin.earnings.columns.editsCreatedEarnings'),
        field: 'editsCreatedEarnings',
        type: ['number'],
        valueFormatter: formatPrice,
        width: DOLLAR_AMOUNT_COLUMN_WIDTH,
      },
      {
        headerName: t('admin.earnings.columns.bonusEarnings'),
        field: 'bonusEarnings',
        type: ['number'],
        valueFormatter: formatPrice,
        width: DOLLAR_AMOUNT_COLUMN_WIDTH,
      },
      {
        headerName: t('admin.earnings.columns.partnershipEarnings'),
        field: 'partnershipEarnings',
        type: ['number'],
        valueFormatter: formatPrice,
        width: DOLLAR_AMOUNT_COLUMN_WIDTH,
      },
    ]),
  })),
  addRef('gridRef', typedAs<AgGridReact | null>(null)),
  addState('newDateInput', 'setNewDateInput', ''),
  addHandlers({
    onChangeNewDateInput:
      ({ setNewDateInput }) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setNewDateInput(event.target.value)
      },
  }),
  addState(
    'creatorCostInput',
    'setCreatorCostInput',
    ({ creatorFundCosts }) => ({
      costPerMediaView: creatorFundCosts.costPerMediaView.toString(),
      costPerProductView: creatorFundCosts.costPerProductView.toString(),
      costPerOutgoingProductClick:
        creatorFundCosts.costPerOutgoingProductClick.toString(),
      costPerMediaPublished: creatorFundCosts.costPerMediaPublished.toString(),
      costPerMediaTagged: creatorFundCosts.costPerMediaTagged.toString(),
      costPerEditCreated: creatorFundCosts.costPerEditCreated.toString(),
    })
  ),
  addStateHandlers(
    ({ creatorCostInput }) => ({
      input: creatorCostInput,
    }),
    {
      onChangeCostPerMediaView:
        ({ input }) =>
        (costPerMediaView: string) => ({
          input: { ...input, costPerMediaView },
        }),
      onChangeCostPerProductView:
        ({ input }) =>
        (costPerProductView: string) => ({
          input: { ...input, costPerProductView },
        }),
      onChangeCostPerOutgoingProductClick:
        ({ input }) =>
        (costPerOutgoingProductClick: string) => ({
          input: { ...input, costPerOutgoingProductClick },
        }),
      onChangeCostPerMediaPublished:
        ({ input }) =>
        (costPerMediaPublished: string) => ({
          input: { ...input, costPerMediaPublished },
        }),
      onChangeCostPerMediaTagged:
        ({ input }) =>
        (costPerMediaTagged: string) => ({
          input: { ...input, costPerMediaTagged },
        }),
      onChangeCostPerEditCreated:
        ({ input }) =>
        (costPerEditCreated: string) => ({
          input: { ...input, costPerEditCreated },
        }),
    }
  ),
  addNavigate,
  addHandlers({
    onSave:
      ({ input: { ...input }, api }) =>
      () => {
        api.creatorEarnings
          .updateCostsAndEarnings({
            costPerMediaView: parseFloat(input.costPerMediaView),
            costPerProductView: parseFloat(input.costPerProductView),
            costPerOutgoingProductClick: parseFloat(
              input.costPerOutgoingProductClick
            ),
            costPerMediaPublished: parseFloat(input.costPerMediaPublished),
            costPerMediaTagged: parseFloat(input.costPerMediaTagged),
            costPerEditCreated: parseFloat(input.costPerEditCreated),
          })
          .then(() => window.location.reload())
      },
    onGenerateDate:
      ({ newDateInput, api }) =>
      () => {
        api.creatorEarnings
          .generateDate({ date: newDateInput })
          .then(() => window.location.reload())
      },
    onSelect:
      ({ navigate }) =>
      (event: RowClickedEvent) => {
        if (event.data?.date) {
          navigate(makeDayEarningsRoute(event.data.date))
        }
      },
    onGridReady:
      ({ gridRef, creatorEarningsOverview }) =>
      () => {
        gridRef.current?.api?.setPinnedTopRowData(
          getOverviewAggregateRow(creatorEarningsOverview)
        )
      },
    onFilterChanged:
      ({ creatorEarningsOverview }) =>
      (event: FilterChangedEvent) => {
        const dates: string[] = []
        event.api.forEachNodeAfterFilter((node) => dates.push(node.data.date))

        event.api.setPinnedTopRowData(
          getOverviewAggregateRow(
            creatorEarningsOverview.filter((earnings) =>
              dates.includes(earnings.date)
            )
          )
        )
      },
    onExport:
      ({ gridRef, t }) =>
      () => {
        gridRef.current!.api.exportDataAsCsv({
          fileName: t('admin.earnings.export.overview'),
        })
      },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    creatorEarningsOverview,
    columns,
    gridRef,
    input: {
      costPerMediaView,
      costPerProductView,
      costPerOutgoingProductClick,
      costPerMediaPublished,
      costPerMediaTagged,
      costPerEditCreated,
    },
    onChangeCostPerMediaView,
    onChangeCostPerProductView,
    onChangeCostPerOutgoingProductClick,
    onChangeCostPerMediaPublished,
    onChangeCostPerMediaTagged,
    onChangeCostPerEditCreated,
    newDateInput,
    onChangeNewDateInput,
    onSave,
    onSelect,
    onGenerateDate,
    onExport,
    onGridReady,
    onFilterChanged,
    classes,
    t,
  }) => (
    <Page>
      <div className={classes.formRow}>
        <div className={classes.actionColumn}>
          <NumberFieldNew
            label={t('admin.earnings.costs.mediaView')}
            value={costPerMediaView}
            onChange={onChangeCostPerMediaView}
          />
          <NumberFieldNew
            label={t('admin.earnings.costs.productView')}
            value={costPerProductView}
            onChange={onChangeCostPerProductView}
          />
          <NumberFieldNew
            label={t('admin.earnings.costs.outgoingProductClick')}
            value={costPerOutgoingProductClick}
            onChange={onChangeCostPerOutgoingProductClick}
          />
          <NumberFieldNew
            label={t('admin.earnings.costs.mediaPublished')}
            value={costPerMediaPublished}
            onChange={onChangeCostPerMediaPublished}
          />
          <NumberFieldNew
            label={t('admin.earnings.costs.mediaTagged')}
            value={costPerMediaTagged}
            onChange={onChangeCostPerMediaTagged}
          />
          <NumberFieldNew
            label={t('admin.earnings.costs.editCreated')}
            value={costPerEditCreated}
            onChange={onChangeCostPerEditCreated}
          />
          <Button
            type="fullWidth"
            text={t('admin.earnings.costs.update')}
            onClick={onSave}
          />
        </div>
        <div className={classes.actionColumn}>
          <Button
            type="fullWidth"
            text={t('admin.earnings.export.button')}
            onClick={onExport}
          />
        </div>
        <div className={classes.actionColumn}>
          <input
            type="date"
            id="newDateInput"
            name="newDateInput"
            value={newDateInput}
            onChange={onChangeNewDateInput}
          />
          <Button
            type="fullWidth"
            text={t('admin.earnings.generateForDate')}
            onClick={onGenerateDate}
          />
        </div>
      </div>
      <div className={cx('ag-theme-alpine', classes.table)}>
        <AgGridReact
          ref={gridRef}
          columnDefs={columns}
          columnTypes={COLUMN_TYPES}
          defaultColDef={DEFAULT_COL_DEF}
          rowData={creatorEarningsOverview}
          onRowDoubleClicked={onSelect}
          onGridReady={onGridReady}
          onFilterChanged={onFilterChanged}
        />
      </div>
    </Page>
  )
)
